// components/Section.jsx
import React from "react";
import { Box, Typography, Button, IconButton, Divider } from "@mui/material";
import LayersIcon from "@mui/icons-material/Layers";
import DeleteIcon from "@mui/icons-material/Delete";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";

// Static/content modules
import Banner from "./Banner";
import Header from "./Header";
import SubHeader from "./SubHeader";
import Paragraph from "./Paragraph";
import RectangleLinkButton from "../components/RectangleLinkButton";
import SiteInformationViewer from "../components/SiteInformationViewer";
import UserDetails from "../components/UserDetails";

// Field/display modules
import AnswerField from "./AnswerField";
import CheckboxField from "./CheckboxField";
import CheckboxGroupField from "./CheckboxGroupField";
import RadioButtonGroupField from "./RadioButtonFieldGroup";
import DoubleShortAnswerField from "./DoubleShortAnswerField";
import DateTimeField from "./DateTimeField";
import DateRangeField from "./DateRangeField";
import TimeRangeField from "./TimeRangeField";
import SignatureField from "./SignatureField";
import UserMultiSelectResponse from "./UserMultiSelectResponse";
import CustomDocumentUploaderView from "./CustomViewDocumentUploader";
import TableField from "./TableField";
import DynamicQuestionGroupField from "./DynamicQuestionGroupField";
import EmailField from "./EmailField";
import DropdownSelectField from "./DropdownSelectField";
import InspectionItemField from "./InspectionItemField";
import AuditCheckboxGroupField from "./AuditCheckboxGroupField";
import RoofPlanViewer from "../components/RoofPlanViewer";

// Upload handlers
import {
  uploadPhoto,
  uploadImage,
  handleFileChangeUpload,
} from "./uploadHandlers";

const matchesStatus = (module, responseStatus) => {
  const v = module.editableWhenStatus;
  if (v == null) return [1, 2, 3, 6, 7, 11].includes(Number(responseStatus));
  const arr = Array.isArray(v) ? v.map(Number) : [Number(v)];
  return arr.includes(Number(responseStatus));
};

const Section = ({
  module,
  styles,
  fieldKey,
  isEditable,
  responseStatus,
  userId,
  setUserId,
  setCompanyId,
  // from ViewResponsePage
  responseData = {},
  handleFieldChange,
  setResponseData,
  signatures = [],
  handleSignatureChange,
  handleRemoveSignature,
  documentStatuses = {},
  formId,
  responseId,
  siteId,
  companyId,
  responseRequestedTo,
  openHazardPopup,
  openZoneHazardPopup,
  onUploadPhoto = (key, file) => uploadPhoto({ docId: key, file }),
  onUploadImage = (key, files) => uploadImage({ docId: key, file: files[0] }),
  setLoading,
  setSuccess,

  // no-ops if not passed
  handleTableCellChange = () => {},
  handleAddTableRow = () => {},
  handleRemoveTableRow = () => {},
  handleDateRangeChange = () => {},
  handleTimeRangeChange = () => {},
  handleRemoveImageFromResponse = () => {},
  handleRemovePhoto = () => {},
}) => {
  const noop = () => {};

  // Section title: prefer module.label, fall back to module.title or "Section"
  const sectionTitle = module.label || module.title || "Section";

  return (
    <Box
      sx={{
        border: "2px solid #c7d2fe",
        borderRadius: 2,
        overflow: "hidden",
        my: 2,
        backgroundColor: "#fff",
      }}
    >
      {/* Section header bar — matches builder style */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 2,
          py: 1.25,
          backgroundColor: "#eef2ff",
          borderBottom: "1.5px solid #c7d2fe",
        }}
      >
        <LayersIcon sx={{ color: "#4f46e5", fontSize: 18 }} />
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 600, color: "#4f46e5", fontSize: "0.875rem" }}
        >
          {sectionTitle}
        </Typography>
      </Box>

      {/* Section content */}
      <Box sx={{ p: 2 }}>
        {module.children?.map((child) => {
          const dataKey = child.id;
          const baseKey = dataKey;
          const value = responseData[dataKey];
          const onChange = handleFieldChange;

          const childIsEditable = isEditable && matchesStatus(child, responseStatus);

          switch (child.type) {
            // ───── Static / Content ─────
            case "banner":
              return <Banner key={baseKey} module={child} styles={styles} />;
            case "header":
              return <Header key={baseKey} text={child.text} styles={styles} />;
            case "subheader":
              return (
                <SubHeader key={baseKey} text={child.text} styles={styles} />
              );
            case "paragraph":
              return <Paragraph key={baseKey} content={child.content} />;
            case "rectangle_link_button":
              return (
                <RectangleLinkButton
                  key={baseKey}
                  title={child.label}
                  url={child.url}
                  buttonColour={styles.buttonColor}
                  openInNewTab={child.openInNewTab}
                />
              );
            case "site_information_viewer":
              return (
                <SiteInformationViewer
                  key={baseKey}
                  siteId={siteId}
                  responses={responseData}
                  handleChange={handleFieldChange}
                  isEditable={childIsEditable}
                />
              );
            case "user_details":
              return (
                <UserDetails
                  key={baseKey}
                  data={responseData[dataKey] || responseData.user_details || {}}
                  onChange={(field, v) =>
                    handleFieldChange(`${dataKey}.${field}`, v)
                  }
                  isEditable={childIsEditable}
                  styles={styles}
                  multiCompanies={!!child.multiCompanies}
                  setUserId={setUserId || (() => {})}
                  setCompanyId={setCompanyId || (() => {})}
                  userId={userId}
                />
              );
            case "roofSiteInformationModule":
              return (
                <RoofPlanViewer
                  key={baseKey}
                  siteId={siteId}
                  responses={responseData}
                  handleChange={handleFieldChange}
                  isEditable={childIsEditable}
                />
              );

            // ───── Simple Fields ─────
            case "short_answer":
            case "long_answer":
              return (
                <AnswerField
                  key={baseKey}
                  module={child}
                  answer={value || ""}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                />
              );
            case "checkbox":
              return (
                <CheckboxField
                  key={baseKey}
                  module={child}
                  value={Boolean(value)}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                />
              );
            case "checkbox_group":
              return (
                <CheckboxGroupField
                  key={baseKey}
                  module={child}
                  value={value || []}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                />
              );
            case "custom_checkbox_group":
              return (
                <CheckboxGroupField
                  key={baseKey}
                  module={child}
                  value={value || []}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                  isCustom
                />
              );

            // ───── Audit ─────
            case "inspection_item":
              return (
                <InspectionItemField
                  key={baseKey}
                  module={child}
                  value={value}
                  isEditable={childIsEditable}
                  onChange={onChange}
                  fieldKey={dataKey}
                  formId={formId}
                />
              );
            case "audit_checkbox_group":
              return (
                <AuditCheckboxGroupField
                  key={baseKey}
                  module={child}
                  value={value}
                  isEditable={childIsEditable}
                  onChange={onChange}
                  fieldKey={dataKey}
                />
              );

            // ───── Zone Hazard (nested) ─────
            case "roofZoneHazardRegisterModule":
            case "zoneHazardModule":
              return (
                <Box key={baseKey} my={2} display="flex" justifyContent="center">
                  <Button
                    variant="outlined"
                    onClick={() =>
                      (openZoneHazardPopup || noop)(child.zoneName || "Roof")
                    }
                    sx={{
                      borderColor: "#d00000",
                      color: "#d00000",
                      "&:hover": {
                        borderColor: "#a10000",
                        backgroundColor: "rgba(208,0,0,0.08)",
                      },
                    }}
                  >
                    {child.label || "View Zone Hazard Register"}
                  </Button>
                </Box>
              );
            case "radio_button_group":
              return (
                <RadioButtonGroupField
                  key={baseKey}
                  module={child}
                  value={value || ""}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                />
              );
            case "double_short_answer":
              return (
                <DoubleShortAnswerField
                  key={baseKey}
                  module={child}
                  value={value?.answers ? value : { answers: ["", ""] }}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={handleFieldChange}
                />
              );

            // ───── Date / Time ─────
            case "datetime":
              return (
                <DateTimeField
                  key={baseKey}
                  module={child}
                  value={value || null}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                />
              );
            case "date_range":
              return (
                <DateRangeField
                  key={baseKey}
                  module={child}
                  value={value || {}}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={(fld, v) => handleDateRangeChange(dataKey, fld, v)}
                />
              );
            case "time_range":
              return (
                <TimeRangeField
                  key={baseKey}
                  module={child}
                  value={value || {}}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={(fld, v) => handleTimeRangeChange(dataKey, fld, v)}
                />
              );

            // ───── Signature ─────
            case "signature":
              const sig = signatures.find((s) => s.module_id === child.id);
              return (
                <SignatureField
                  key={baseKey}
                  module={child}
                  fieldKey={dataKey}
                  signatureData={sig ? sig.signature_data : ""}
                  isEditable={childIsEditable}
                  onSignatureChange={(dataURL) =>
                    handleSignatureChange(child.id, dataURL)
                  }
                  onRemove={() => handleRemoveSignature(child.id)}
                />
              );

            // ───── Advanced Fields ─────
            case "user_multi_select":
              return (
                <UserMultiSelectResponse
                  key={baseKey}
                  module={child}
                  value={value || []}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                  companyId={companyId}
                  requestedTo={responseRequestedTo}
                />
              );
            case "custom_document_uploader":
              return (
                <CustomDocumentUploaderView
                  key={baseKey}
                  module={child}
                  file={responseData.documents?.[child.id]}
                  isEditable={childIsEditable}
                  onChange={(file) =>
                    handleFileChangeUpload(child.id, file, setResponseData)
                  }
                />
              );
            case "table":
              return (
                <TableField
                  key={baseKey}
                  module={child}
                  value={value || []}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onCellChange={handleTableCellChange}
                  onAddRow={handleAddTableRow}
                  onRemoveRow={handleRemoveTableRow}
                />
              );
            case "dynamic_question_group":
              return (
                <DynamicQuestionGroupField
                  key={baseKey}
                  module={child}
                  responses={responseData}
                />
              );
            case "email":
              return (
                <EmailField
                  key={baseKey}
                  module={child}
                  value={value || responseData.email || ""}
                  isEditable={childIsEditable}
                  fieldKey={dataKey}
                  onChange={onChange}
                />
              );
            case "dropdown_select":
            case "email_dropdown":
              return (
                <DropdownSelectField
                  key={baseKey}
                  module={child}
                  value={value || (child.multiple ? [] : "")}
                  isEditable={childIsEditable}
                  onChange={onChange}
                  fieldKey={dataKey}
                />
              );

            // ───── Images ─────
            case "image":
              return (
                <Box key={baseKey} textAlign="center" my={2}>
                  <img
                    src={child.url}
                    alt={child.alt_text}
                    style={{ maxWidth: "100%" }}
                  />
                </Box>
              );
            case "image_upload":
              const uploadedImages = Array.isArray(value) ? value : [];
              return (
                <Box key={baseKey} my={2}>
                  <Typography variant="h6">{child.label}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {uploadedImages.map((img, idx) => (
                      <Box key={idx} sx={{ position: "relative" }}>
                        <img
                          src={img}
                          alt={`Uploaded ${idx + 1}`}
                          style={{ maxWidth: 150 }}
                        />
                        {isEditable && (
                          <IconButton
                            sx={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              color: "white",
                            }}
                            onClick={() =>
                              handleRemoveImageFromResponse(dataKey, idx)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                  {isEditable && (
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      component="label"
                      sx={{ mt: 1 }}
                    >
                      Upload Images
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={(e) =>
                          e.target.files.length > 0 &&
                          onUploadImage(dataKey, e.target.files)
                        }
                      />
                    </Button>
                  )}
                  {isEditable && (
                    <Typography variant="caption" color="textSecondary">
                      {uploadedImages.length}/{child.imageLimit || "∞"} images
                      uploaded.
                    </Typography>
                  )}
                  <Divider sx={{ mt: 2, borderColor: "grey.300" }} />
                </Box>
              );
            case "photo_gallery":
              const gallery = Array.isArray(value) ? value : [];
              return (
                <Box key={baseKey} my={2}>
                  <Typography variant="h6">{child.label}</Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
                    {gallery.map((photo, idx) => (
                      <Box key={idx} sx={{ position: "relative" }}>
                        <img
                          src={photo}
                          alt={`Photo ${idx + 1}`}
                          style={{ maxWidth: 150 }}
                        />
                        {isEditable && (
                          <IconButton
                            sx={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              color: "white",
                            }}
                            onClick={() => handleRemovePhoto(dataKey, idx)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        )}
                      </Box>
                    ))}
                  </Box>
                  {isEditable && (
                    <Button
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      component="label"
                      sx={{ mt: 1 }}
                    >
                      Upload Photo
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        onChange={(e) =>
                          e.target.files[0] &&
                          onUploadPhoto(dataKey, e.target.files[0])
                        }
                      />
                    </Button>
                  )}
                </Box>
              );

            // ───── Hazard / Docs ─────
            case "hazard_register":
              return (
                <Box key={baseKey} my={2}>
                  <Typography variant="h6">{child.label}</Typography>
                  <Button variant="outlined" onClick={openHazardPopup}>
                    Hazard &amp; Risk Register Audit
                  </Button>
                </Box>
              );
            case "document_checker":
              const status = documentStatuses[child.id];
              let url = child.formUrl || "";
              return (
                <Box
                  key={baseKey}
                  my={2}
                  p={2}
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: 2,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant="h6">{child.documentName}</Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    {status ? (
                      <CheckCircleIcon color="success" />
                    ) : (
                      <>
                        {url && (
                          <Button
                            variant="text"
                            onClick={() => {
                              const p = new URLSearchParams();
                              p.append("response", responseId);
                              if (siteId) p.append("siteId", siteId);
                              window.open(
                                url +
                                  (url.includes("?") ? "&" : "?") +
                                  p.toString(),
                                "_blank",
                              );
                            }}
                          >
                            Click here to complete
                          </Button>
                        )}
                        <CancelIcon color="error" />
                      </>
                    )}
                  </Box>
                </Box>
              );

            // ───── Fallback ─────
            default:
              return (
                <pre key={baseKey} style={{ color: "red" }}>
                  {JSON.stringify(child, null, 2)}
                </pre>
              );
          }
        })}
      </Box>
    </Box>
  );
};

export default Section;